import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Kpi, Pill, Loading, Err, tableStyles, EmptyState } from "./_shared";

export default function Credits() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/credits/global").then(setData).catch((e) => setErr(e.message));
  }, []);

  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  return (
    <div data-testid="admin-credits">
      <PageHead title="Credit ledger" sub="Global view across all workspaces" />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        <Kpi label="Available · total" value={(data.totals?.available ?? 0).toLocaleString()} />
        <Kpi label="Reserved · total" value={(data.totals?.reserved ?? 0).toLocaleString()} sub="held by in-flight jobs" />
        <Kpi label="Workspaces tracked" value={(data.balances || []).length} />
        <Kpi label="Recent transactions" value={(data.transactions || []).length} sub="last 200 entries" />
      </div>

      <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16 }}>
        <Card title="Top workspaces by balance">
          {(data.balances || []).length === 0 ? <EmptyState title="No balances yet" /> : (
            <table style={tableStyles.table}>
              <thead><tr>
                <th style={tableStyles.th}>Workspace</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Available</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Reserved</th>
              </tr></thead>
              <tbody>
                {data.balances.map((b) => (
                  <tr key={b.workspace_id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={tableStyles.td}>
                      <Link to={`/admin/workspaces/${b.workspace_id}`} style={{ color: "var(--pl-aqua)", fontWeight: 700 }}>{b.workspace_name}</Link>
                      <div style={{ fontSize: 10, color: "var(--pl-text-muted)", fontFamily: "JetBrains Mono, monospace" }}>{b.workspace_id}</div>
                    </td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", fontWeight: 800, color: "var(--pl-aqua)" }}>
                      {(b.available_balance ?? 0).toLocaleString()}
                    </td>
                    <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{b.reserved_balance ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Recent transactions (all workspaces)">
          {(data.transactions || []).length === 0 ? <EmptyState title="No transactions yet" /> : (
            <div style={{ maxHeight: 540, overflow: "auto" }}>
              <table style={tableStyles.table}>
                <thead><tr>
                  <th style={tableStyles.th}>When</th>
                  <th style={tableStyles.th}>Workspace</th>
                  <th style={tableStyles.th}>Type</th>
                  <th style={{ ...tableStyles.th, textAlign: "right" }}>Amount</th>
                  <th style={tableStyles.th}>Reason</th>
                </tr></thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr key={t.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                      <td style={{ ...tableStyles.td, fontSize: 11 }}>{(t.created_at || "").slice(0, 19)}</td>
                      <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(t.workspace_id || "").slice(0, 12)}</td>
                      <td style={tableStyles.td}><Pill tone={t.amount >= 0 ? "success" : "warn"}>{t.type}</Pill></td>
                      <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", color: t.amount >= 0 ? "var(--pl-aqua)" : "#fcd34d" }}>
                        {t.amount >= 0 ? `+${t.amount}` : t.amount}
                      </td>
                      <td style={{ ...tableStyles.td, fontSize: 11 }}>{t.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
